import React, { useContext, useState } from 'react';
import { Button, Card, Space, Typography, Upload, message, Modal } from 'antd';
import { FileTextOutlined, EyeOutlined, UploadOutlined, DeleteOutlined, InboxOutlined, EditOutlined } from '@ant-design/icons';
import { AuthContext } from "../../../context/AuthContext";
import { PageTitle } from "../../../components/PageTitle";
import { useQueryClient } from "@tanstack/react-query";
import { uploadCVDocument, deleteCVDocument, getCandidateDetails } from "../../../services/api";
import styled from "styled-components";
import { RcFile } from 'antd/es/upload/interface';
import { useSubscriptionEndDate } from "../../../hooks/useSubscriptionEndDate";
import { SilverButton } from "../../../components/LayoutWrapperCandidate";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Dragger } = Upload;

export const CVDocument: React.FC = () => {
    const { user, setUser } = useContext(AuthContext);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const isClient = user?.client_id && user.client_id > 0;
    const {endDate} = useSubscriptionEndDate(!isClient);
    const hasActiveSubscription = !isClient && endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    const handleUpload = async (file: RcFile): Promise<boolean> => {
        // Validate file type
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        const isValidType = allowedTypes.includes(file.type);

        if (!isValidType) {
            message.error('Nur PDF, DOC und DOCX Dateien sind erlaubt!');
            return false;
        }

        // Validate file size (10MB)
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('Die Datei darf nicht größer als 10MB sein!');
            return false;
        }

        setUploading(true);

        try {
            // Upload file - backend automatically attaches it to candidate record
            await uploadCVDocument(file);

            // Refresh candidate data to get updated cv_doc
            const updatedCandidate = await getCandidateDetails();
            if (user) {
                setUser({ ...user, _candidate: updatedCandidate });
            }

            // Invalidate queries to ensure fresh data
            queryClient.invalidateQueries({ queryKey: ['candidate'] });
            queryClient.invalidateQueries({ queryKey: ['user'] });

            message.success('Lebenslauf erfolgreich hochgeladen!');
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Fehler beim Hochladen des Lebenslaufs');
        } finally {
            setUploading(false);
        }

        return false; // Prevent default upload behavior
    };

    const handleView = () => {
        if (user?._candidate?.cv_doc?.url) {
            window.open(user._candidate.cv_doc.url, '_blank');
        }
    };

    const handleDelete = () => {
        Modal.confirm({
            title: 'Lebenslauf löschen?',
            content: 'Möchten Sie Ihren Lebenslauf wirklich löschen?',
            okText: 'Löschen',
            cancelText: 'Abbrechen',
            okType: 'danger',
            onOk: async () => {
                setDeleting(true);
                try {
                    // Delete CV - backend automatically removes it from candidate record
                    await deleteCVDocument();

                    // Refresh candidate data to get updated cv_doc
                    const updatedCandidate = await getCandidateDetails();
                    if (user) {
                        setUser({ ...user, _candidate: updatedCandidate });
                    }

                    // Invalidate queries to ensure fresh data
                    queryClient.invalidateQueries({ queryKey: ['candidate'] });
                    queryClient.invalidateQueries({ queryKey: ['user'] });

                    message.success('Lebenslauf erfolgreich gelöscht');
                } catch (error) {
                    console.error('Delete error:', error);
                    message.error('Fehler beim Löschen des Lebenslaufs');
                } finally {
                    setDeleting(false);
                }
            }
        });
    };

    const hasCV = user?._candidate?.cv_doc?.url;

    return (
        <ResponsiveSpace direction="vertical" size="large">
            <PageTitle title="Lebenslauf" />

            <StyledCard>
                {hasCV ? (
                    <Space direction="vertical" className="w-full" size="large">
                        <div style={{ textAlign: 'center' }}>
                            <FileTextOutlined style={{ fontSize: 64, color: '#1890ff' }} />
                            <Title level={4} style={{ marginTop: 16 }}>Ihr Lebenslauf</Title>
                            <Text type="secondary">
                                Sie haben bereits einen Lebenslauf hochgeladen
                            </Text>
                        </div>

                        <ButtonContainer>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => navigate('/cv-generator')}
                                size="large"
                            >
                                Lebenslauf erstellen
                            </Button>
                            <Button
                                type="primary"
                                icon={<EyeOutlined />}
                                onClick={handleView}
                                size="large"
                            >
                                Anzeigen
                            </Button>
                            <Upload
                                beforeUpload={handleUpload}
                                showUploadList={false}
                                accept=".pdf,.doc,.docx"
                            >
                                <Button
                                    icon={<UploadOutlined />}
                                    loading={uploading}
                                    size="large"
                                >
                                    Ersetzen
                                </Button>
                            </Upload>
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={handleDelete}
                                loading={deleting}
                                size="large"
                            >
                                Löschen
                            </Button>
                        </ButtonContainer>
                    </Space>
                ) : hasActiveSubscription ? (
                    <Space direction="vertical" className="w-full" size="large">
                        <div style={{ textAlign: 'center' }}>
                            <Title level={4}>Lebenslauf hochladen</Title>
                            <Text type="secondary">
                                Laden Sie Ihren Lebenslauf als PDF, DOC oder DOCX hoch (max. 10MB)
                            </Text>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => navigate('/cv-generator')}
                                size="large"
                            >
                                Oder Lebenslauf erstellen
                            </Button>
                        </div>

                        <Dragger
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            accept=".pdf,.doc,.docx"
                            disabled={uploading}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Klicken oder Datei hierher ziehen
                            </p>
                            <p className="ant-upload-hint">
                                PDF, DOC, DOCX - Maximale Größe: 10MB
                            </p>
                        </Dragger>

                        {uploading && (
                            <div style={{ textAlign: 'center' }}>
                                <Text type="secondary">Wird hochgeladen...</Text>
                            </div>
                        )}
                    </Space>
                ) : (
                    <PaywallContainer>
                        <FileTextOutlined style={{ fontSize: 64, color: '#999', marginBottom: 16 }} />
                        <Title level={4}>Lebenslauf hochladen</Title>
                        <Text type="secondary" style={{ textAlign: 'center', marginBottom: 24 }}>
                            Erhöhen Sie Ihre Chancen mit einem professionellen Lebenslauf.
                            Upgraden Sie zu <Text strong>Silver+</Text> um Ihren Lebenslauf hochzuladen und automatisch bei Kontaktaufnahmen mitzusenden.
                        </Text>
                        <SilverButton onClick={() => navigate("/silverplus")}>
                            silver+ entdecken
                        </SilverButton>
                    </PaywallContainer>
                )}
            </StyledCard>
        </ResponsiveSpace>
    );
};

const ResponsiveSpace = styled(Space)`
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
`;

const StyledCard = styled(Card)`
    width: 100%;

    @media (max-width: 576px) {
        .ant-card-body {
            padding: 16px;
        }
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;

    @media (max-width: 576px) {
        flex-direction: column;

        button {
            width: 100%;
        }
    }
`;

const PaywallContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
`;
